import ApplicationController from './application_controller';

// Toggles between two signer-entry modes on the service-log signing form:
//
//   dropdown mode — pick an existing signer (Patient / LegalGuardian /
//                   AuthorizedSigner) from the Select2-enhanced dropdown.
//   inline mode   — type a new signer's name + relationship + optional email.
//                   Submitting always creates a new AuthorizedSigner row
//                   linked to this patient (no "save / don't save" toggle —
//                   opening this panel is itself the affirmation).
//
// The user toggles via buttons rather than a dropdown sentinel option, so
// Stimulus doesn't need to coordinate with Select2's event handling.
//
// In inline mode:
//   - the dropdown wrapper is hidden entirely
//   - the inline name + relationship inputs are required
//   - the visual-confirmation callout is shown above the signature pad so the
//     signer can catch typos before signing
//   - the consent-text display flips to the authorized_signer variant
//
// In dropdown mode:
//   - inline fields are hidden, values cleared
//   - dropdown is visible and required
export default class extends ApplicationController {
  static targets = [
    'select',
    'dropdownWrapper',
    'guidance',
    'fields',
    'confirmation',
    'nameInput',
    'relationshipSelect',
    'relationshipCustom',
    'emailInput',
    'displayName',
    'displayRelationship',
    'displayEmail',
    'displayEmailGroup'
  ];

  static values = { mode: { type: String, default: 'dropdown' } };

  connect() {
    this.applyState();
  }

  enterInlineMode(event) {
    if (event) event.preventDefault();
    this.modeValue = 'inline';
  }

  enterDropdownMode(event) {
    if (event) event.preventDefault();
    this.modeValue = 'dropdown';
  }

  modeValueChanged() {
    this.applyState();
  }

  refreshConfirmation() {
    if (this.hasDisplayNameTarget) {
      this.displayNameTarget.textContent = this.titleize(this.nameInputTarget.value) || '—';
    }
    if (this.hasDisplayRelationshipTarget) {
      this.displayRelationshipTarget.textContent = this.currentRelationship() || '—';
    }
    if (this.hasDisplayEmailGroupTarget && this.hasDisplayEmailTarget) {
      const email = this.hasEmailInputTarget ? this.emailInputTarget.value.trim() : '';
      this.displayEmailGroupTarget.hidden = !email;
      this.displayEmailTarget.textContent = email;
    }
  }

  applyState() {
    const isInline = this.modeValue === 'inline';

    if (this.hasDropdownWrapperTarget) this.dropdownWrapperTarget.hidden = isInline;
    // "Verify the person below + Manage signers" text only makes sense in
    // dropdown mode. Hide when the user has chosen the one-time-signer path.
    if (this.hasGuidanceTarget) this.guidanceTarget.hidden = isInline;
    // Patient-portal mode renders the form without the inline-signer fields /
    // visual-confirmation row (LGs/18+ patients pick from a roster and never
    // type a one-time signer). The controller still mounts on the wrapper
    // div, so guard the targets rather than throw on the missing nodes.
    if (this.hasFieldsTarget) this.fieldsTarget.hidden = !isInline;
    if (this.hasConfirmationTarget) this.confirmationTarget.hidden = !isInline;

    if (this.hasNameInputTarget) this.nameInputTarget.required = isInline;
    if (this.hasRelationshipSelectTarget) this.relationshipSelectTarget.required = isInline;

    // Disabled form controls aren't submitted, so the controller never sees a
    // stale signer_id when we're in inline mode (defense in depth on top of
    // the model's xor validation + DB CHECK constraint).
    // The dropdown isn't rendered at all when the patient has no signer
    // options (no eligible self-sign, no guardian, no authorized signer);
    // in that case mode starts in 'inline' and the target may be absent.
    // Required is intentionally NOT toggled here — see the ERB comment on the
    // select for why HTML5 required + Selectize don't compose, and the
    // server-side AuthorizedSignature#signer_identity_present validation
    // that backs us up.
    if (this.hasSelectTarget) {
      this.selectTarget.disabled = isInline;
      if (isInline) this.selectTarget.value = '';
    }

    if (isInline) {
      this.refreshConfirmation();
    } else {
      this.clearInlineFields();
    }

    this.updateConsentDisplay(isInline);
  }

  // Mirrors the same consent-template swap the dropdown's `onchange` handler
  // does for FK-backed signers. For inline mode we always use the
  // authorized_signer variant since the signer is acting in that capacity.
  updateConsentDisplay(isInline) {
    if (!isInline) return; // dropdown onchange handler manages this in dropdown mode
    const display  = document.getElementById('consent-display');
    const template = document.getElementById('consent-authorized-signer-template');
    if (display && template) display.innerHTML = template.innerHTML;
  }

  clearInlineFields() {
    if (this.hasNameInputTarget) this.nameInputTarget.value = '';
    if (this.hasRelationshipSelectTarget) {
      this.relationshipSelectTarget.value = '';
      // Notify relationship-select controller so its custom-input toggle
      // resets — otherwise a previously-revealed "Other" text input stays
      // visible/enabled when the user re-enters inline mode.
      this.relationshipSelectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.hasRelationshipCustomTarget) this.relationshipCustomTarget.value = '';
    if (this.hasEmailInputTarget) this.emailInputTarget.value = '';
  }

  currentRelationship() {
    if (this.hasRelationshipCustomTarget && !this.relationshipCustomTarget.disabled) {
      return this.relationshipCustomTarget.value;
    }
    const value = this.relationshipSelectTarget.value;
    return value === '__other__' ? '' : value;
  }

  titleize(str) {
    return (str || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }
}
